<!--
  Global notification banner shown in the app bar (driven by the
  `notifications` Vuex store). The icon name comes from the notification's
  i18n entry (`notifications.<type>.icon`, a legacy `mdi-*` string), which is
  mapped to a Lucide component here (the mdi icon font is removed).
-->
<script setup lang="ts">
import { computed, markRaw, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { Eye, Link, ScanEye, UserCheck, UserPen } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import { useNotificationsStore } from '@/stores/notifications';

const notificationsStore = useNotificationsStore();
const { t } = useI18n();

/**
 * Legacy mdi icon names (still stored in the locale files) mapped to Lucide
 * components. Unknown names render no icon.
 */
const MDI_TO_LUCIDE: Record<string, Component> = markRaw({
  'mdi-account-eye': Eye,
  'mdi-account-edit': UserPen,
  'mdi-account-check': UserCheck,
  'mdi-monitor-eye': ScanEye,
  'mdi-link': Link,
});

const notification = computed(() => notificationsStore.notification);

const icon = computed<Component | null>(() => {
  const iconString = t(`notifications.${notification.value.messageType}.icon`);
  // A missing i18n entry returns the key itself ("notifications.x.icon"),
  // which we detect by the dots — real icon names have none.
  if (iconString.includes('.')) return null;
  return MDI_TO_LUCIDE[iconString] ?? null;
});
</script>

<template>
  <div
    v-if="notification"
    class="flex rounded p-2 justify-between items-center w-full"
    :class="[`notification--${notification.styleType}`]"
    style="height: 52px"
  >
    <div class="overflow-hidden flex items-center">
      <component :is="icon" v-if="icon" class="text-primary mr-2 size-6 shrink-0" />
      <div class="text-sm text-primary" style="line-height: 1.1">
        <span v-if="notification.messageType">
          {{ t(`notifications.${notification.messageType}.message`) }}
        </span>
        <span v-else>
          {{ notification.messageString }}
        </span>
      </div>
    </div>
    <div v-if="notification.action || notification.actionSecondary" class="flex items-center gap-2">
      <Button
        v-if="notification.actionSecondary"
        variant="outline"
        @click="notification.actionSecondary"
      >
        {{ t(`notifications.${notification.messageType}.actionSecondaryTitle`) }}
      </Button>

      <Button @click="notification.action">
        <span v-if="notification.messageType">
          {{ t(`notifications.${notification.messageType}.actionTitle`) }}
        </span>
        <span v-else>{{ t('common.ok') }}</span>
      </Button>
    </div>
  </div>
</template>

<style>
/* Panel colors from the legacy hub palette (variables.scss / Vuetify theme). */
.notification--warning {
  background: rgba(251, 186, 4, 0.2);
  border: 1px solid #fbba04;
}
.notification--error {
  background: rgba(228, 89, 102, 0.2);
  border: 1px solid #ff7352;
}
.notification--success {
  background: rgba(0, 194, 138, 0.2);
  border: 1px solid #00c28a;
}
.notification--info {
  background: white;
  color: color-mix(in oklab, var(--color-primary) 40%, white);
  border: 1px solid color-mix(in oklab, var(--color-primary) 40%, white);
}
</style>
